import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  ApiResponse,
  AppError,
  ClubClassDTO,
  ClassBookingDTO,
  BulkAssignDTO,
  CancelClassDTO,
  GetClubClassesParams,
  GetMyBookingsParams,
  BookClassParams,
  CancelBookingParams,
  CreateClubClassRequest,
  UpdateClubClassRequest,
  AddMemberToClassRequest,
  BulkAssignMembersRequest,
  ClubClassSeriesDTO,
  GetClubClassSeriesListParams,
  CreateClubClassSeriesRequest,
  UpdateClubClassSeriesRequest,
  GenerateSeriesOccurrencesParams,
  GenerateSeriesOccurrencesResponse,
  CreateClubClassResponse,
  UpdateClubClassResponse,
  CreateClubClassSeriesResponse,
  UpdateClubClassSeriesResponse,
} from "@membentry/shared-types";
import type { AxiosInstance } from "axios";

type ApiClientLikeError = AppError & {
  status?: number;
  originalError?: unknown;
  isApiClientError?: true;
};

export const CLUB_CLASSES_QUERY_KEY = "club-classes";
export const MY_BOOKINGS_QUERY_KEY = "my-bookings";
export const CLASS_ATTENDEES_QUERY_KEY = "class-attendees";
export const CLUB_CLASS_SERIES_QUERY_KEY = "club-class-series";
export const RECURRING_CLASS_ENROLLMENTS_MUTATION_KEY = "recurring-class-enrollments";
export const RECURRING_CLASS_ENROLLMENTS_QUERY_KEY = "recurring-class-enrollments-query";

export const classKeys = {
  all: [CLUB_CLASSES_QUERY_KEY] as const,
  lists: () => [...classKeys.all, "list"] as const,
  list: (clubId: number, params: GetClubClassesParams) =>
    [...classKeys.lists(), clubId, params] as const,
  details: () => [...classKeys.all, "detail"] as const,
  detail: (clubId: number, classId: number) =>
    [...classKeys.details(), clubId, classId] as const,
};

export const myBookingKeys = {
  all: [MY_BOOKINGS_QUERY_KEY] as const,
  list: (clubId: number) => [...myBookingKeys.all, clubId] as const,
};

export const classAttendeeKeys = {
  all: [CLASS_ATTENDEES_QUERY_KEY] as const,
  lists: () => [...classAttendeeKeys.all, "list"] as const,
  listRoot: (clubId: number) => [...classAttendeeKeys.lists(), clubId] as const,
  list: (clubId: number, classId: number) =>
    [...classAttendeeKeys.listRoot(clubId), classId] as const,
};

export const classSeriesKeys = {
  all: [CLUB_CLASS_SERIES_QUERY_KEY] as const,
  lists: () => [...classSeriesKeys.all, "list"] as const,
  list: (clubId: number, params: GetClubClassSeriesListParams) =>
    [...classSeriesKeys.lists(), clubId, params] as const,
  details: () => [...classSeriesKeys.all, "detail"] as const,
  detail: (clubId: number, seriesId: number) =>
    [...classSeriesKeys.details(), clubId, seriesId] as const,
};

export const recurringClassEnrollmentKeys = {
  all: [RECURRING_CLASS_ENROLLMENTS_QUERY_KEY] as const,
  lists: () => [...recurringClassEnrollmentKeys.all, "list"] as const,
  list: (clubId: number, seriesId: number) =>
    [...recurringClassEnrollmentKeys.lists(), clubId, seriesId] as const,
};

type QueryClient = ReturnType<typeof useQueryClient>;

function invalidateClassListQueries(queryClient: QueryClient, clubId?: number) {
  queryClient.invalidateQueries({
    queryKey: clubId == null ? classKeys.all : classKeys.lists(),
    ...(clubId == null ? {} : { predicate: (query) => query.queryKey[2] === clubId }),
  });
}

function invalidateClassDetailQueries(
  queryClient: QueryClient,
  clubId: number,
  classId?: number,
) {
  queryClient.invalidateQueries({
    queryKey:
      classId == null
        ? classKeys.details()
        : classKeys.detail(clubId, classId),
    ...(classId == null ? { predicate: (query) => query.queryKey[2] === clubId } : {}),
  });
}

function invalidateClassAttendeeQueries(
  queryClient: QueryClient,
  clubId?: number,
  classId?: number,
) {
  if (clubId == null) {
    queryClient.invalidateQueries({ queryKey: classAttendeeKeys.all });
    return;
  }

  if (classId == null) {
    queryClient.invalidateQueries({
      queryKey: classAttendeeKeys.lists(),
      predicate: (query) => query.queryKey[2] === clubId,
    });
    return;
  }

  queryClient.invalidateQueries({
    queryKey: classAttendeeKeys.list(clubId, classId),
  });
}

function invalidateClassSeriesListQueries(
  queryClient: QueryClient,
  clubId?: number,
) {
  queryClient.invalidateQueries({
    queryKey: clubId == null ? classSeriesKeys.all : classSeriesKeys.lists(),
    ...(clubId == null ? {} : { predicate: (query) => query.queryKey[2] === clubId }),
  });
}

function invalidateClassSeriesDetailQueries(
  queryClient: QueryClient,
  clubId: number,
  seriesId?: number,
) {
  queryClient.invalidateQueries({
    queryKey:
      seriesId == null
        ? classSeriesKeys.details()
        : classSeriesKeys.detail(clubId, seriesId),
    ...(seriesId == null ? { predicate: (query) => query.queryKey[2] === clubId } : {}),
  });
}

function invalidateMyBookingQueries(queryClient: QueryClient, clubId?: number) {
  queryClient.invalidateQueries({
    queryKey: clubId == null ? myBookingKeys.all : myBookingKeys.list(clubId),
  });
}

function invalidateRecurringClassEnrollmentQueries(
  queryClient: QueryClient,
  clubId?: number,
  seriesId?: number,
) {
  if (clubId == null || seriesId == null) {
    queryClient.invalidateQueries({
      queryKey: recurringClassEnrollmentKeys.all,
    });
    return;
  }

  queryClient.invalidateQueries({
    queryKey: recurringClassEnrollmentKeys.list(clubId, seriesId),
  });
}

function updateSeriesTouchesRecurringEnrollmentContracts(
  update: UpdateClubClassSeriesRequest,
): boolean {
  return (
    update.maxCapacity != null
    || update.requiredMembershipPlanId != null
    || update.clearRequiredMembershipPlan === true
  );
}

function invalidateClassMutationQueries(
  queryClient: QueryClient,
  clubId: number,
  classId?: number,
) {
  invalidateClassListQueries(queryClient, clubId);
  invalidateClassDetailQueries(queryClient, clubId, classId);
  invalidateClassAttendeeQueries(queryClient, clubId, classId);
}

// ─── Member Hooks ───────────────────────────────────────────────

export function useGetClubClassesQuery(
  client: AxiosInstance,
  params: GetClubClassesParams,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubClassDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { clubId } = params;
  return useQuery({
    queryKey: classKeys.list(clubId, params),
    queryFn: async () => {
      const { getClubClasses } = await import("@membentry/shared-api");
      return getClubClasses(client, params);
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useGetClubClassQuery(
  client: AxiosInstance,
  clubId: number,
  classId: number,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubClassDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: classKeys.detail(clubId, classId),
    queryFn: async () => {
      const { getClubClass } = await import("@membentry/shared-api");
      return getClubClass(client, clubId, classId);
    },
    enabled: !!clubId && !!classId,
    ...options,
  });
}

export function useGetMyBookingsQuery(
  client: AxiosInstance,
  params: GetMyBookingsParams,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClassBookingDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { clubId } = params;
  return useQuery({
    queryKey: myBookingKeys.list(clubId),
    queryFn: async () => {
      const { getMyBookings } = await import("@membentry/shared-api");
      return getMyBookings(client, params);
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useBookClassMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<ClassBookingDTO>,
      ApiClientLikeError,
      BookClassParams
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (params: BookClassParams) => {
      const { bookClass } = await import("@membentry/shared-api");
      return bookClass(client, params);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassMutationQueries(queryClient, variables.clubId, variables.classId);
      invalidateMyBookingQueries(queryClient, variables.clubId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useCancelBookingMutation(
  client: AxiosInstance,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<void>,
      ApiClientLikeError,
      CancelBookingParams
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (params: CancelBookingParams) => {
      const { cancelBooking } = await import("@membentry/shared-api");
      return cancelBooking(client, params);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassMutationQueries(queryClient, variables.clubId, variables.classId);
      invalidateMyBookingQueries(queryClient, variables.clubId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

// ─── Owner Hooks ────────────────────────────────────────────────

export function useCreateClubClassMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<
      CreateClubClassResponse,
      Error,
      CreateClubClassRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (data: CreateClubClassRequest) => {
      const { createClubClass } = await import("@membentry/shared-api");
      return createClubClass(client, clubId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassListQueries(queryClient, clubId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useUpdateClubClassMutation(
  client: AxiosInstance,
  clubId: number,
  classId: number,
  options?: Omit<
    UseMutationOptions<
      UpdateClubClassResponse,
      Error,
      UpdateClubClassRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (data: UpdateClubClassRequest) => {
      const { updateClubClass } = await import("@membentry/shared-api");
      return updateClubClass(client, clubId, classId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassMutationQueries(queryClient, clubId, classId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useCancelClassMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<ApiResponse<CancelClassDTO>, Error, number>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (classId: number) => {
      const { cancelClass } = await import("@membentry/shared-api");
      return cancelClass(client, clubId, classId);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassMutationQueries(queryClient, clubId, variables);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useAddMemberToClassMutation(
  client: AxiosInstance,
  clubId: number,
  classId: number,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<ClassBookingDTO>,
      Error,
      AddMemberToClassRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (data: AddMemberToClassRequest) => {
      const { addMemberToClass } = await import("@membentry/shared-api");
      return addMemberToClass(client, clubId, classId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassMutationQueries(queryClient, clubId, classId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useRemoveMemberFromClassMutation(
  client: AxiosInstance,
  clubId: number,
  classId: number,
  options?: Omit<
    UseMutationOptions<ApiResponse<void>, Error, string>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (memberId: string) => {
      const { removeMemberFromClass } = await import("@membentry/shared-api");
      return removeMemberFromClass(client, clubId, classId, memberId);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassMutationQueries(queryClient, clubId, classId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useBulkAssignMembersMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<BulkAssignDTO>,
      Error,
      BulkAssignMembersRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (data: BulkAssignMembersRequest) => {
      const { bulkAssignMembers } = await import("@membentry/shared-api");
      return bulkAssignMembers(client, clubId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassMutationQueries(queryClient, clubId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useGetClassAttendeesQuery(
  client: AxiosInstance,
  clubId: number,
  classId: number,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClassBookingDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: classAttendeeKeys.list(clubId, classId),
    queryFn: async () => {
      const { getClassAttendees } = await import("@membentry/shared-api");
      return getClassAttendees(client, clubId, classId);
    },
    enabled: !!clubId && !!classId,
    ...options,
  });
}

// ─── Series Hooks ──────────────────────────────────────────────

export function useGetClubClassSeriesListQuery(
  client: AxiosInstance,
  params: GetClubClassSeriesListParams,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubClassSeriesDTO[]>>,
    "queryKey" | "queryFn"
  >,
) {
  const { clubId } = params;
  return useQuery({
    queryKey: classSeriesKeys.list(clubId, params),
    queryFn: async () => {
      const { getClubClassSeriesList } = await import("@membentry/shared-api");
      return getClubClassSeriesList(client, params);
    },
    enabled: !!clubId,
    ...options,
  });
}

export function useGetClubClassSeriesQuery(
  client: AxiosInstance,
  clubId: number,
  seriesId: number,
  options?: Omit<
    UseQueryOptions<ApiResponse<ClubClassSeriesDTO>>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery({
    queryKey: classSeriesKeys.detail(clubId, seriesId),
    queryFn: async () => {
      const { getClubClassSeries } = await import("@membentry/shared-api");
      return getClubClassSeries(client, clubId, seriesId);
    },
    enabled: !!clubId && !!seriesId,
    ...options,
  });
}

export function useCreateClubClassSeriesMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<
      CreateClubClassSeriesResponse,
      Error,
      CreateClubClassSeriesRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (data: CreateClubClassSeriesRequest) => {
      const { createClubClassSeries } = await import("@membentry/shared-api");
      return createClubClassSeries(client, clubId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassSeriesListQueries(queryClient, clubId);
      invalidateClassListQueries(queryClient, clubId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useUpdateClubClassSeriesMutation(
  client: AxiosInstance,
  clubId: number,
  seriesId: number,
  options?: Omit<
    UseMutationOptions<
      UpdateClubClassSeriesResponse,
      Error,
      UpdateClubClassSeriesRequest
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (data: UpdateClubClassSeriesRequest) => {
      const { updateClubClassSeries } = await import("@membentry/shared-api");
      return updateClubClassSeries(client, clubId, seriesId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassSeriesListQueries(queryClient, clubId);
      invalidateClassSeriesDetailQueries(queryClient, clubId, seriesId);
      invalidateClassListQueries(queryClient, clubId);
      if (updateSeriesTouchesRecurringEnrollmentContracts(variables)) {
        invalidateRecurringClassEnrollmentQueries(queryClient, clubId, seriesId);
      }
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useCancelClubClassSeriesMutation(
  client: AxiosInstance,
  clubId: number,
  options?: Omit<
    UseMutationOptions<ApiResponse<void>, Error, number>,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (seriesId: number) => {
      const { cancelClubClassSeries } = await import("@membentry/shared-api");
      return cancelClubClassSeries(client, clubId, seriesId);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassSeriesListQueries(queryClient, clubId);
      invalidateClassSeriesDetailQueries(queryClient, clubId, variables);
      invalidateClassListQueries(queryClient, clubId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

export function useGenerateSeriesOccurrencesMutation(
  client: AxiosInstance,
  clubId: number,
  seriesId: number,
  options?: Omit<
    UseMutationOptions<
      ApiResponse<GenerateSeriesOccurrencesResponse>,
      Error,
      GenerateSeriesOccurrencesParams | undefined
    >,
    "mutationFn"
  >,
) {
  const queryClient = useQueryClient();
  const { onSuccess, onSettled, ...mutationOptions } = options ?? {};

  return useMutation({
    mutationFn: async (data?: GenerateSeriesOccurrencesParams) => {
      const { generateSeriesOccurrences } = await import("@membentry/shared-api");
      return generateSeriesOccurrences(client, clubId, seriesId, data);
    },
    onSuccess: async (data, variables, _onMutateResult, context) => {
      invalidateClassSeriesListQueries(queryClient, clubId);
      invalidateClassSeriesDetailQueries(queryClient, clubId, seriesId);
      invalidateClassMutationQueries(queryClient, clubId);
      await onSuccess?.(data, variables, undefined, context);
    },
    onSettled: async (data, error, variables, _onMutateResult, context) => {
      await onSettled?.(data, error, variables, undefined, context);
    },
    ...mutationOptions,
  });
}

// ─── Invalidation Helpers ───────────────────────────────────────

export function invalidateClubClasses(
  queryClient: ReturnType<typeof useQueryClient>,
  clubId?: number,
) {
  invalidateClassListQueries(queryClient, clubId);
}

export function invalidateMyBookings(
  queryClient: ReturnType<typeof useQueryClient>,
  clubId?: number,
) {
  invalidateMyBookingQueries(queryClient, clubId);
}

export function invalidateClassAttendees(
  queryClient: ReturnType<typeof useQueryClient>,
  clubId?: number,
  classId?: number,
) {
  invalidateClassAttendeeQueries(queryClient, clubId, classId);
}

export function invalidateClubClassSeries(
  queryClient: ReturnType<typeof useQueryClient>,
  clubId?: number,
) {
  invalidateClassSeriesListQueries(queryClient, clubId);
}

export function invalidateRecurringClassEnrollments(
  queryClient: ReturnType<typeof useQueryClient>,
  clubId?: number,
  seriesId?: number,
) {
  invalidateRecurringClassEnrollmentQueries(queryClient, clubId, seriesId);
}
